import React from 'react';
import type { AttributeInputKind } from '@/api/models';
import {
    sanitizeSlugInput,
    slugify,
    inputKindHasValues,
} from '../attributesMeta';
import { useResetOnKeyChange } from '@/utils';

export type AttributeTypeFormMode = 'create' | 'edit';

export type AttributeTypeFormValues = {
    name: string;
    slug: string;
    inputKind: AttributeInputKind;
    isMultiSelect: boolean;
};

type UseAttributeTypeFormParams = {
    initialValues?: AttributeTypeFormValues;
    isOpen: boolean;
    mode?: AttributeTypeFormMode;
    onSubmit: (input: AttributeTypeFormValues) => void;
};

const EMPTY_INITIAL: AttributeTypeFormValues = {
    name: '',
    slug: '',
    inputKind: 'Select',
    isMultiSelect: false,
};

/**
 * Form state for the Add / Edit attribute-type modal. Mirrors
 * `useCategoryForm`: auto-derive the slug from the name in create mode until
 * the slug is hand-edited, and re-seed every field from `initialValues` on
 * each open/close transition (the modal stays mounted). Multi-select only
 * applies to `Select` kinds, so it's forced off otherwise.
 */
export const useAttributeTypeForm = ({
    initialValues,
    isOpen,
    mode = 'create',
    onSubmit,
}: UseAttributeTypeFormParams) => {
    const initial = initialValues ?? EMPTY_INITIAL;

    const [name, setName] = React.useState(initial.name);
    const [slug, setSlug] = React.useState(initial.slug);
    const [inputKind, setInputKind] = React.useState<AttributeInputKind>(
        initial.inputKind,
    );
    const [isMultiSelect, setIsMultiSelect] = React.useState(
        initial.isMultiSelect,
    );
    const [isSlugEdited, setIsSlugEdited] = React.useState(mode === 'edit');

    // Re-seed on open/close transitions only — see useCategoryForm.
    useResetOnKeyChange(isOpen, () => {
        setName(initial.name);
        setSlug(initial.slug);
        setInputKind(initial.inputKind);
        setIsMultiSelect(initial.isMultiSelect);
        setIsSlugEdited(mode === 'edit');
    });

    const onNameChange = React.useCallback(
        (value: string) => {
            setName(value);
            if (!isSlugEdited) {
                setSlug(slugify(value));
            }
        },
        [isSlugEdited],
    );

    const onSlugChange = React.useCallback((value: string) => {
        setIsSlugEdited(true);
        setSlug(sanitizeSlugInput(value));
    }, []);

    const onInputKindChange = React.useCallback((value: AttributeInputKind) => {
        setInputKind(value);
        if (!inputKindHasValues(value)) {
            setIsMultiSelect(false);
        }
    }, []);

    const onMultiSelectChange = React.useCallback((value: boolean) => {
        setIsMultiSelect(value);
    }, []);

    const trimmedName = name.trim();
    const isValid = trimmedName.length > 0 && slug.length > 0;
    const supportsMultiSelect = inputKindHasValues(inputKind);

    // Parent owns the close decision (close on success, stay open with the
    // inline error on failure) — see useCategoryForm.
    const onSubmitClick = React.useCallback(() => {
        if (!isValid) {
            return;
        }
        onSubmit({
            name: trimmedName,
            slug,
            inputKind,
            isMultiSelect: supportsMultiSelect ? isMultiSelect : false,
        });
    }, [
        inputKind,
        isMultiSelect,
        isValid,
        onSubmit,
        slug,
        supportsMultiSelect,
        trimmedName,
    ]);

    return {
        name,
        slug,
        inputKind,
        isMultiSelect,
        isValid,
        supportsMultiSelect,
        onNameChange,
        onSlugChange,
        onInputKindChange,
        onMultiSelectChange,
        onSubmit: onSubmitClick,
    };
};
